import { observer } from 'mobx-react'
import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router'

// evertel
import { Analytics } from '@evertel/analytics'
import { useService } from '@evertel/di'
import { validateEmail } from '@evertel/utils'
import { Links } from '@evertel/constants'
import { InfoBox, Card, CardBody, Col, Button, Row, PasswordInput, FormInput, useUI, Icon } from '@evertel/web/ui'
import { RegistrationData } from '../RegistrationStepperUnauthorized'
import { VerifiedEmailDomainController } from '@evertel/shared/verified-email-domain'

interface Step1RegisterProps {
    registrationData: RegistrationData
    updateRegistrationData: (field: keyof RegistrationData, value: string) => void
    onStepComplete: () => Promise<void>
    isCrisis?: boolean
    isLoading: boolean
}

type NameErrors = {
    firstName?: string
    lastName?: string
}

const MAX_CHARACTERS_ALLOWED = 64
const ALLOWED_NAME_CHARACTERS = /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžæÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð\s,.'\-\d()]+$/u

const Step1Register: React.FC<Step1RegisterProps> = observer(({
    registrationData,
    updateRegistrationData,
    onStepComplete
}) => {

    const analytics = useService(Analytics)
    const verifiedEmailDomainController = useService(VerifiedEmailDomainController)

    const [isPasswordValid, setIsPasswordValid] = useState(false)
    const [isEmailValid, setIsEmailValid] = useState(false)
    const [emailError, setEmailError] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [nameErrors, setNameErrors] = useState<NameErrors>({})

    const { isConfirmed } = useUI()

    useEffect(() => {
        analytics.logView('/register/create-account')
    }, [])

    const hasNameErrors = useMemo(() =>
        Object.values(nameErrors).some(error => Boolean(error))
    , [nameErrors])

    const validateNameField = (field: string, value: string) => {
        let errorMsg = ''

        if (field === 'firstName' || field === 'lastName') {
            const fieldTrim = value.trim()
            const fieldDisplay = field === 'firstName' ? 'First name' : 'Last name'

            if (!fieldTrim) {
                errorMsg = `${fieldDisplay} is required`
            } else if (fieldTrim.length > MAX_CHARACTERS_ALLOWED) {
                errorMsg = `Maximum ${MAX_CHARACTERS_ALLOWED} characters allowed`
            } else if (!ALLOWED_NAME_CHARACTERS.test(fieldTrim)) {
                errorMsg = 'Invalid characters entered'
            }
        }

        setNameErrors(prev => ({
            ...prev,
            [field]: errorMsg || undefined
        }))
    }

    const onChange = (e) => {
        const { name, value } = e.target
        updateRegistrationData(name, value)

        if (name === 'firstName' || name === 'lastName') {
            validateNameField(name, value)
        }

        if (name === 'email') {
            setIsEmailValid(validateEmail(value))
            setEmailError('')
        }
    }

    const validateEmailField = (e) => {
        if (!isEmailValid) {
            setEmailError('Email seems to be invalid')
        } else {
            setEmailError('')
        }
    }

    const createAccount = async () => {
        const { firstName, lastName, email, password } = registrationData

        const firstNameTrim = firstName?.trim()
        const lastNameTrim = lastName?.trim()
        const emailAndPasswordCheck = email?.toLowerCase() === password?.toLowerCase()

        let errorMsg = ''

        // check for errors
        if (!firstName || !lastName || !email || !password || !firstNameTrim.length || !lastNameTrim.length) {
            // make sure all fields are filled out
            errorMsg = 'Please fill out all fields'
        } else if (!isPasswordValid) {
            // make sure password meets minimum strength score
            errorMsg = 'Your password does not meet our minimum strength requirements. Please revise and try again'
        } else if (!validateEmail(email)) {
            // make sure email is valid
            errorMsg = 'Please enter a valid email address'
        } else if (emailAndPasswordCheck) {
            errorMsg = 'Your password cannot match your work email. Please change it and try again'
        }

        // if there's an error, show InfoBox and stop
        if (errorMsg) {
            setError(errorMsg)
            return
        }

        setIsLoading(true)

        // check that email domain is a government email
        const emailDomain = email.substring(email.lastIndexOf('@') + 1)
        const isGovernmentEmail = await verifiedEmailDomainController.getVerifiedEmailDomain(emailDomain)

        updateRegistrationData('isGovernmentEmail', isGovernmentEmail as any)

        if (!isGovernmentEmail) {
            const confirmed = await isConfirmed({
                title: 'Is this your work email?',
                message: `<p class="h4 mb-4"><strong><span>${email}</span> does not appear to be a government email.</strong></p>It is highly recommended to use your work email with Evertel. If this is your work email. Click "Continue with this email".`,
                acceptButton: {
                    label: 'Continue with this Email',
                    color: 'primary'
                },
                cancelButton: {
                    label: 'Change Email',
                    color: 'link'
                }
            })

            if (!confirmed) {
                setIsLoading(false)
                return
            }
        }

        //after we do all this checks we're ready to create the account
        try {
            await onStepComplete()

        } catch (registrationError: any) {
            //Do nothing, it's managed by RegistrationStepper
        } finally {
            setIsLoading(false)
        }
    }

    const isFirstNameValid = registrationData.firstName?.trim()
    const isLastNameValid = registrationData.lastName?.trim()
    const isEmailValidAndPresent = isEmailValid && registrationData.email?.trim().length > 0
    const isPasswordValidAndPresent = isPasswordValid && registrationData.password?.trim().length > 0

    const allFieldsComplete = isFirstNameValid && isLastNameValid && isEmailValidAndPresent && isPasswordValidAndPresent

    return (
        <Row className="fadeIn animated m-4">
            <Col style={{ maxWidth: 600 }}>
                <Card>
                    <CardBody className="p-4">
                        <div className="text-center">
                            <img
                                src={'/assets/img/connect-logo-110x110.png'}
                                width="70"
                                alt="Evertel logo"
                            />
                            <h1>Welcome to Evertel</h1>
                        </div>
                        <div className="d-flex justify-content-center mt-3 mb-3">
                            Already have an account?
                            <Link to="/login">&nbsp;&nbsp; Login here</Link>
                        </div>
                        {error &&
                                <InfoBox color="danger">
                                    {error}
                                </InfoBox>
                        }

                        {/* FIRST NAME */}
                        <div className="mb-3 mt-4">
                            <FormInput
                                name="firstName"
                                value={registrationData.firstName}
                                label="First Name"
                                placeholder="Enter your first name"
                                required
                                disabled={isLoading}
                                onChange={onChange}
                                error={`${nameErrors.firstName ? nameErrors.firstName : ''}`}
                            />
                        </div>

                        {/* LAST NAME */}
                        <div className="mb-3">
                            <FormInput
                                name="lastName"
                                value={registrationData.lastName}
                                label="Last Name"
                                placeholder="Enter your last name"
                                required
                                disabled={isLoading}
                                onChange={onChange}
                                error={`${nameErrors.lastName ? nameErrors.lastName : ''}`}
                            />
                        </div>

                        {/* EMAIL */}
                        <div className="mb-3">
                            <FormInput
                                name="email"
                                value={registrationData.email}
                                label="Enter your email"
                                placeholder="You will use this to login"
                                required
                                disabled={isLoading}
                                onChange={onChange}
                                onBlur={validateEmailField}
                                error={emailError}
                                leftIcon="at"
                            />
                        </div>

                        {/* PASSWORD */}
                        <PasswordInput
                            label="Enter your password"
                            value={registrationData.password}
                            onChange={(value) => updateRegistrationData('password', value)}
                            onValidate={setIsPasswordValid}
                            disabled={isLoading}
                            className="mb-3"
                            userData={registrationData}
                            enablePasswordValidation
                            showPreviousPasswordRequirement={false}
                        />
                        <Icon name="asterisk" color="danger" />
                        <i><small>{' = required'}</small></i>

                        {/* SUBMIT BTTN */}
                        <Button
                            color="primary"
                            size="lg"
                            type="submit"
                            disabled={isLoading || !allFieldsComplete || hasNameErrors}
                            loading={isLoading}
                            block
                            onClick={createAccount}
                            className="mt-4 mb-2 w-100"
                        >
                            Create Account
                        </Button>

                        {/* TERMS OF USE & PRIVACY */}
                        <div className="text-center">
                            <div className="py-4">
                                <small>
                                    <span>By creating an account with Evertel I agree to the </span>
                                    <a href={Links.termsOfService} className="btn btn-link fs-6" style={{ fontSize: 'revert' }} target="_blank" rel="noopener noreferrer"> Terms of Use </a>
                                    <span> and </span>
                                    <a href={Links.privacyPolicy} className="btn btn-link fs-6" style={{ fontSize: 'revert' }} target="_blank" rel="noopener noreferrer"> Privacy Policy </a>
                                </small>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    )
})

export { Step1Register }
